// ============================================================
//  src/pages/bots/tabs/PersistentMenuTab.js
// ============================================================
import React, { useCallback, useEffect, useState } from 'react';
import { getMenu, saveMenu, publishMenu, clearMenu } from '../../../api/client';
import toast from 'react-hot-toast';

const MAX_ITEMS  = 3;
const MAX_TITLE  = 24;
const MAX_DESC   = 72;

const EMPTY_ITEM = () => ({ title: '', description: '', payload: '' });

function autoPayload(title) {
  return title.toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '');
}

// ── Phone mock ───────────────────────────────────────────────
function PhoneMock({ items }) {
  return (
    <div style={styles.phone}>
      <div style={styles.phoneBar}>
        <span style={{ fontWeight: 700, fontSize: 13 }}>WhatsApp</span>
      </div>
      <div style={styles.chatArea}>
        <div style={styles.bubble}>Hi! How can I help you today? 👋</div>
      </div>
      <div style={styles.inputRow}>
        <div style={styles.hamburger}>
          <div style={styles.hamLine} />
          <div style={styles.hamLine} />
          <div style={styles.hamLine} />
        </div>
        <div style={styles.fakeInput}>Type a message…</div>
        <div style={styles.micIcon}>🎤</div>
      </div>
      {items.length > 0 && (
        <div style={styles.menuPopup}>
          <div style={styles.menuPopupTitle}>Menu</div>
          {items.map((item, i) => (
            <div key={i} style={styles.menuPopupItem}>
              <div style={styles.menuPopupItemTitle}>{item.title || `Item ${i + 1}`}</div>
              {item.description && (
                <div style={styles.menuPopupItemDesc}>{item.description}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Item editor row ───────────────────────────────────────────
function ItemRow({ item, index, total, onChange, onRemove, onMoveUp, onMoveDown }) {
  const titleLen = item.title.length;
  const descLen  = item.description.length;

  return (
    <div style={styles.itemRow}>
      <div style={styles.itemRowHeader}>
        <span style={styles.itemIndex}>#{index + 1}</span>
        <div style={{ display: 'flex', gap: 6 }}>
          <button style={styles.iconBtn} onClick={onMoveUp}  disabled={index === 0}           title="Move up">↑</button>
          <button style={styles.iconBtn} onClick={onMoveDown} disabled={index === total - 1}  title="Move down">↓</button>
          <button style={{ ...styles.iconBtn, color: 'var(--danger, #e53e3e)' }} onClick={onRemove} title="Remove">✕</button>
        </div>
      </div>

      <div style={styles.fieldGroup}>
        <label style={styles.fieldLabel}>
          Title <span style={styles.required}>*</span>
          <span style={styles.counter}>{titleLen}/{MAX_TITLE}</span>
        </label>
        <input
          style={{ ...styles.input, borderColor: titleLen > MAX_TITLE ? 'var(--danger, #e53e3e)' : undefined }}
          value={item.title}
          maxLength={MAX_TITLE}
          placeholder="e.g. Check Balance"
          onChange={e => {
            const val = e.target.value;
            onChange({ ...item, title: val, payload: autoPayload(val) });
          }}
        />
      </div>

      <div style={styles.fieldGroup}>
        <label style={styles.fieldLabel}>
          Description
          <span style={styles.counter}>{descLen}/{MAX_DESC}</span>
        </label>
        <input
          style={styles.input}
          value={item.description}
          maxLength={MAX_DESC}
          placeholder="Short subtitle shown under the title (optional)"
          onChange={e => onChange({ ...item, description: e.target.value })}
        />
      </div>

      <div style={styles.fieldGroup}>
        <label style={styles.fieldLabel}>
          Payload
          <span style={styles.fieldHint}> — sent to your bot when the user taps</span>
        </label>
        <input
          style={{ ...styles.input, fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}
          value={item.payload}
          placeholder="AUTO_GENERATED"
          onChange={e => onChange({ ...item, payload: e.target.value.toUpperCase() })}
        />
      </div>
    </div>
  );
}

// ── Main Tab ──────────────────────────────────────────────────
export default function PersistentMenuTab({ bId, bot }) {
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [publish,  setPublish]  = useState(false);
  const [deleting, setDeleting] = useState(false);

  const hasPhoneId = !!bot?.bPhoneNumberId;
  const hasToken   = !!bot?.bToken;
  const canPublish = hasPhoneId && hasToken;

  // ── Load ──────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMenu(bId);
      if (res.data?.success) setItems(res.data.menu_items ?? []);
      else toast.error(res.data?.message ?? 'Failed to load menu');
    } catch {
      toast.error('Network error');
    }
    setLoading(false);
  }, [bId]);

  useEffect(() => { load(); }, [load]);

  // ── Helpers ───────────────────────────────────────────────
  function addItem() {
    if (items.length >= MAX_ITEMS) return;
    setItems(prev => [...prev, EMPTY_ITEM()]);
  }

  function removeItem(i) {
    setItems(prev => prev.filter((_, idx) => idx !== i));
  }

  function changeItem(i, updated) {
    setItems(prev => prev.map((it, idx) => idx === i ? updated : it));
  }

  function moveItem(i, dir) {
    setItems(prev => {
      const arr  = [...prev];
      const swap = i + dir;
      if (swap < 0 || swap >= arr.length) return arr;
      [arr[i], arr[swap]] = [arr[swap], arr[i]];
      return arr;
    });
  }

  function validate() {
    for (const [i, it] of items.entries()) {
      if (!it.title.trim())          { toast.error(`Item #${i + 1} needs a title`);    return false; }
      if (it.title.length > MAX_TITLE) { toast.error(`Item #${i + 1} title too long`); return false; }
    }
    return true;
  }

  // ── API calls ─────────────────────────────────────────────
  async function handleSave() {
    if (!validate()) return;
    setSaving(true);
    try {
      const res = await saveMenu(bId, items);
      if (res.data?.success) { toast.success('Menu saved'); setItems(res.data.menu_items); }
      else toast.error(res.data?.message ?? 'Save failed');
    } catch { toast.error('Network error'); }
    setSaving(false);
  }

  async function handlePublish() {
    if (!validate()) return;
    setPublish(true);
    try {
      const res = await publishMenu(bId, items);
      if (res.data?.success) { toast.success('Published to WhatsApp ✓'); setItems(res.data.menu_items); }
      else toast.error(res.data?.message ?? 'Publish failed');
    } catch { toast.error('Network error'); }
    setPublish(false);
  }

  async function handleDelete() {
    if (!window.confirm('Clear the persistent menu from WhatsApp? Users will no longer see the ☰ menu.')) return;
    setDeleting(true);
    try {
      const res = await clearMenu(bId);
      if (res.data?.success) { toast.success('Menu cleared'); setItems([]); }
      else toast.error(res.data?.message ?? 'Delete failed');
    } catch { toast.error('Network error'); }
    setDeleting(false);
  }

  // ── Render ────────────────────────────────────────────────
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 40, color: 'var(--text-muted)' }}>
      <span className="spinner spinner-dark" /> Loading menu…
    </div>
  );

  return (
    <div style={styles.root}>

      {/* Info banner */}
      <div style={styles.infoBanner}>
        <div style={styles.infoBannerIcon}>☰</div>
        <div>
          <div style={styles.infoBannerTitle}>Persistent Menu</div>
          <div style={styles.infoBannerText}>
            The persistent menu appears as a <strong>hamburger icon (☰)</strong> in the WhatsApp message input bar —
            always visible to your users, not just on first open. Up to <strong>3 items</strong>, each with a title
            (max 24 chars) and optional description.
          </div>
        </div>
      </div>

      {/* Warning if missing credentials */}
      {!canPublish && (
        <div style={styles.warnBanner}>
          ⚠️ <strong>Phone Number ID {!hasToken ? 'and WhatsApp token ' : ''}not configured</strong> —
          you can save a draft but cannot publish to WhatsApp until your bot credentials are complete.
        </div>
      )}

      <div style={styles.layout}>

        {/* Left: editor */}
        <div style={styles.editor}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionTitle}>
              Menu Items <span style={styles.count}>{items.length}/{MAX_ITEMS}</span>
            </div>
            {items.length < MAX_ITEMS && (
              <button style={styles.addBtn} onClick={addItem}>+ Add Item</button>
            )}
          </div>

          {items.length === 0 ? (
            <div style={styles.empty}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>☰</div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>No menu items yet</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
                Add up to 3 items that will appear in your bot's persistent menu.
              </div>
              <button style={styles.addBtnLg} onClick={addItem}>+ Add First Item</button>
            </div>
          ) : (
            items.map((item, i) => (
              <ItemRow
                key={i}
                item={item}
                index={i}
                total={items.length}
                onChange={updated => changeItem(i, updated)}
                onRemove={() => removeItem(i)}
                onMoveUp={() => moveItem(i, -1)}
                onMoveDown={() => moveItem(i, 1)}
              />
            ))
          )}

          {/* Action buttons */}
          {items.length > 0 && (
            <div style={styles.actions}>
              <button
                className="btn btn-outline"
                onClick={handleSave}
                disabled={saving}
                style={{ minWidth: 110 }}
              >
                {saving ? <><span className="spinner spinner-dark" /> Saving…</> : '💾 Save Draft'}
              </button>
              <button
                className="btn btn-primary"
                onClick={handlePublish}
                disabled={publish || !canPublish}
                style={{ minWidth: 140 }}
                title={!canPublish ? 'Configure bot credentials first' : undefined}
              >
                {publish ? <><span className="spinner" /> Publishing…</> : '🚀 Publish to WhatsApp'}
              </button>
            </div>
          )}

          {/* Clear section */}
          {canPublish && (
            <div style={styles.dangerSection}>
              <div>
                <div style={styles.dangerLabel}>Clear Persistent Menu</div>
                <div style={styles.dangerDesc}>Removes the ☰ menu from WhatsApp for all users of this bot.</div>
              </div>
              <button
                className="btn"
                style={{ background: 'var(--danger, #e53e3e)', color: '#fff', borderColor: 'var(--danger, #e53e3e)', minWidth: 80 }}
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? <span className="spinner" /> : 'Clear'}
              </button>
            </div>
          )}
        </div>

        {/* Right: phone preview */}
        <div style={styles.preview}>
          <div style={styles.previewLabel}>Live Preview</div>
          <PhoneMock items={items} />
          <div style={styles.previewHint}>
            The ☰ icon appears in the chat input bar. Tapping it reveals your menu items.
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────
const styles = {
  root:            { paddingBottom: 40 },
  infoBanner:      { display: 'flex', gap: 14, padding: '14px 16px', background: 'var(--primary-light, #e6f7f2)', border: '1.5px solid var(--primary, #00c88c)', borderRadius: 'var(--radius-md, 10px)', marginBottom: 20, alignItems: 'flex-start' },
  infoBannerIcon:  { fontSize: 22, flexShrink: 0, marginTop: 1 },
  infoBannerTitle: { fontWeight: 800, fontSize: 14, color: 'var(--text-dark)', marginBottom: 4 },
  infoBannerText:  { fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 },
  warnBanner:      { padding: '12px 16px', background: '#fff8e6', border: '1.5px solid #f5a623', borderRadius: 'var(--radius-md, 10px)', fontSize: 13, color: '#7a5000', marginBottom: 20, lineHeight: 1.5 },
  layout:          { display: 'grid', gridTemplateColumns: '1fr 280px', gap: 24, alignItems: 'flex-start' },
  editor:          { minWidth: 0 },
  sectionHeader:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 },
  sectionTitle:    { fontWeight: 800, fontSize: 15, color: 'var(--text-dark)' },
  count:           { fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginLeft: 6 },
  addBtn:          { background: 'none', border: '1.5px solid var(--primary, #00c88c)', color: 'var(--primary-dark, #009966)', borderRadius: 'var(--radius-md, 10px)', padding: '6px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' },
  addBtnLg:        { background: 'var(--primary, #00c88c)', border: 'none', color: '#fff', borderRadius: 'var(--radius-md, 10px)', padding: '10px 22px', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' },
  empty:           { background: 'var(--white, #fff)', border: '2px dashed var(--border, #e2e8f0)', borderRadius: 'var(--radius-lg, 14px)', padding: '40px 20px', textAlign: 'center', color: 'var(--text-dark)' },
  itemRow:         { background: 'var(--white, #fff)', border: '1.5px solid var(--border, #e2e8f0)', borderRadius: 'var(--radius-lg, 14px)', padding: '16px 18px', marginBottom: 12 },
  itemRowHeader:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 },
  itemIndex:       { fontSize: 11, fontWeight: 800, color: 'var(--primary-dark, #009966)', textTransform: 'uppercase', letterSpacing: '0.6px' },
  iconBtn:         { background: 'none', border: '1px solid var(--border, #e2e8f0)', borderRadius: 6, width: 28, height: 28, cursor: 'pointer', fontSize: 13, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontFamily: 'inherit' },
  fieldGroup:      { marginBottom: 12 },
  fieldLabel:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.5px' },
  required:        { color: 'var(--danger, #e53e3e)', marginLeft: 2 },
  counter:         { fontWeight: 600, fontSize: 11, color: 'var(--text-muted)' },
  fieldHint:       { fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontSize: 11, color: 'var(--text-muted)' },
  input:           { width: '100%', padding: '9px 12px', border: '1.5px solid var(--border, #e2e8f0)', borderRadius: 'var(--radius-md, 10px)', fontSize: 13, fontFamily: 'inherit', color: 'var(--text-dark)', background: 'var(--surface, #f8fafc)', outline: 'none', boxSizing: 'border-box' },
  actions:         { display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' },
  dangerSection:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginTop: 24, padding: '14px 16px', background: 'var(--white, #fff)', border: '1.5px solid var(--border, #e2e8f0)', borderRadius: 'var(--radius-lg, 14px)', flexWrap: 'wrap' },
  dangerLabel:     { fontSize: 13.5, fontWeight: 700, color: 'var(--danger, #e53e3e)', marginBottom: 3 },
  dangerDesc:      { fontSize: 12, color: 'var(--text-muted)' },
  preview:         { position: 'sticky', top: 20 },
  previewLabel:    { fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.7px', color: 'var(--text-muted)', marginBottom: 10, textAlign: 'center' },
  previewHint:     { fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', marginTop: 10, lineHeight: 1.5, padding: '0 8px' },
  phone:           { width: 240, margin: '0 auto', background: '#e5ddd5', borderRadius: 28, border: '3px solid #333', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', position: 'relative', minHeight: 380 },
  phoneBar:        { background: '#075e54', color: '#fff', padding: '10px 14px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 },
  chatArea:        { padding: '14px 12px', minHeight: 160, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' },
  bubble:          { alignSelf: 'flex-start', background: '#fff', borderRadius: '0 12px 12px 12px', padding: '8px 12px', fontSize: 12, color: '#333', maxWidth: '85%', boxShadow: '0 1px 2px rgba(0,0,0,0.1)', lineHeight: 1.4 },
  inputRow:        { display: 'flex', alignItems: 'center', gap: 6, background: '#f0f0f0', padding: '8px 10px', borderTop: '1px solid #ddd' },
  hamburger:       { width: 20, height: 20, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 4, cursor: 'pointer', flexShrink: 0 },
  hamLine:         { height: 2, background: '#555', borderRadius: 2 },
  fakeInput:       { flex: 1, background: '#fff', borderRadius: 20, padding: '6px 12px', fontSize: 11, color: '#aaa' },
  micIcon:         { fontSize: 14, flexShrink: 0 },
  menuPopup:       { position: 'absolute', bottom: 46, left: 0, right: 0, background: '#fff', borderTop: '1px solid #e0e0e0', padding: '8px 0', boxShadow: '0 -2px 8px rgba(0,0,0,0.08)', zIndex: 10 },
  menuPopupTitle:  { fontSize: 10, fontWeight: 800, color: '#888', textTransform: 'uppercase', letterSpacing: '0.6px', padding: '4px 14px 6px', borderBottom: '1px solid #eee', marginBottom: 4 },
  menuPopupItem:   { padding: '7px 14px', borderBottom: '1px solid #f5f5f5', cursor: 'pointer' },
  menuPopupItemTitle: { fontSize: 12, fontWeight: 700, color: '#075e54' },
  menuPopupItemDesc:  { fontSize: 10, color: '#888', marginTop: 1 },
};